import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { HomeScreen } from "./screens/HomeScreen.js";
import { NotesScreen } from "./screens/NotesScreen.js";
import { ResumesScreen } from "./screens/ResumesScreen.js";
import { SettingsContent } from "./screens/SettingsContent.js";
import { DesktopShell } from "./screens/DesktopShell.js";
import { Icons } from "./components/Icons.js";
import { EditWindow } from "./notes/EditWindow.js";
import { EditorView } from "./notes/EditorView.js";
import { SummaryToast } from "./components/SummaryToast.js";
import type { EditTarget } from "./notes/types.js";
import type { Screen } from "./screens/nav.js";
import { useAppearance } from "./theme/useAppearance.js";
import { useIsDesktop } from "./lib/useIsDesktop.js";
import { useAuth } from "./auth/AuthContext.js";
import { useRecording } from "./core/useRecording.js";
import { startQueueProcessing } from "./core/captureQueue.js";
import { OfflineBanner } from "./components/OfflineBanner.js";
import { api, type UsageData } from "./lib/api.js";

const QUALITY_BITS: Record<string, number> = { high: 128_000, medium: 64_000, low: 32_000 };

/**
 * Coquille de l'application (mobile + bureau). La machine à états d'enregistrement vit ici. L'éditeur
 * et la fenêtre d'édition sont des superpositions modales. `reloadToken` rafraîchit listes et quotas
 * après une capture/édition. En interface simple (§7.4), seule la note cumulée est disponible.
 */
export function App() {
  const appearance = useAppearance();
  // Disposition bureau sur grand écran, sauf si l'utilisateur fige l'interface étroite (§12.2).
  const isDesktop = useIsDesktop() && !appearance.forceNarrow;
  const { logout } = useAuth();
  const [screen, setScreen] = useState<Screen>("home");
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  // Les paramètres sont une fenêtre superposée, comme les notes et les résumés — et non un écran :
  // ils obéissent ainsi exactement aux mêmes règles de largeur, dans les deux dispositions.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const bumpReload = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    startQueueProcessing();
  }, []);

  // Réglages serveur (délai de finalisation) + quotas, rechargés après capture/édition.
  const [finalizeDelaySeconds, setFinalizeDelaySeconds] = useState(10);
  useEffect(() => {
    let cancelled = false;
    api
      .settings()
      .then((s) => {
        if (!cancelled && s.preferences?.finalizeDelaySeconds) setFinalizeDelaySeconds(s.preferences.finalizeDelaySeconds);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const [usage, setUsage] = useState<UsageData | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .usage()
      .then((u) => !cancelled && setUsage(u))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);
  // Ratio d'usage pour l'alerte 80 %/100 % : le plus haut entre minutes transcrites et volume audio,
  // afin que le dépassement de l'un OU l'autre quota déclenche le bandeau (§10.4).
  const usageRatio = (() => {
    if (!usage) return null;
    const c = usage.current;
    const minRatio = c.quotaMinutes ? c.transcribedMinutes / c.quotaMinutes : 0;
    const volRatio = c.quotaAudioBytes ? c.audioBytes / c.quotaAudioBytes : 0;
    const ratio = Math.max(minRatio, volRatio);
    return ratio > 0 ? ratio : null;
  })();

  // Nombre d'enregistrements (carte « Mes notes »).
  const [notesCount, setNotesCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    Promise.all([api.listDayNotes(), api.listStandaloneNotes()])
      .then(([d, n]) => !cancelled && setNotesCount(d.length + n.length))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  // Bandeau « enregistré ».
  const [justSaved, setJustSaved] = useState(false);
  const savedTimer = useRef(0);
  const flashSaved = useCallback(() => {
    setJustSaved(true);
    window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setJustSaved(false), 4500);
  }, []);
  useEffect(() => () => window.clearTimeout(savedTimer.current), []);

  const recording = useRecording({
    finalizeDelaySeconds,
    audioBitsPerSecond: QUALITY_BITS[appearance.quality],
    micDeviceId: appearance.micDeviceId,
    onFinalized: () => {
      flashSaved();
      bumpReload();
    },
  });

  // Interface simple : seule la note cumulée existe (§7.4).
  const simple = appearance.interfaceMode === "simple";
  const { mode: recMode, phase: recPhase, setMode: recSetMode } = recording;
  useEffect(() => {
    if (simple && recMode !== "daily" && recPhase === "idle") recSetMode("daily");
  }, [simple, recMode, recPhase, recSetMode]);

  const onWrite = () => setEditorOpen(true);

  return (
    <div
      id="approot"
      className={"app" + (isDesktop ? " is-desktop" : "")}
      style={{ "--tscale": appearance.textSize, "--winmax": `${appearance.winMaxWidth}px` } as CSSProperties}
    >
      <OfflineBanner />
      {isDesktop ? (
        <DesktopShell
          screen={screen}
          setScreen={setScreen}
          recording={recording}
          btnScale={appearance.btnSize}
          simple={simple}
          usageRatio={usageRatio}
          usage={usage}
          onWrite={onWrite}
          onOpen={setEditTarget}
          reloadToken={reloadToken}
          notesCount={notesCount}
          justSaved={justSaved}
          appearance={appearance}
          onChanged={bumpReload}
          onLogout={() => void logout()}
          settingsOpen={settingsOpen}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      ) : (
        <div className="phone">
          <div className="phone-inner">
            {appearance.bgImage ? <div className="bg-layer" style={{ backgroundImage: appearance.bgImage }} /> : null}
            {screen === "home" && (
              <HomeScreen
                recording={recording}
                btnScale={appearance.btnSize}
                simple={simple}
                usageRatio={usageRatio}
                bgDaily={appearance.bgDaily}
                bgStandalone={appearance.bgStandalone}
                onWrite={onWrite}
                onResumes={() => setScreen("resumes")}
                goNotes={() => setScreen("notes")}
                goSettings={() => setSettingsOpen(true)}
                notesCount={notesCount}
                justSaved={justSaved}
              />
            )}
            {screen === "notes" && <NotesScreen onOpen={setEditTarget} reloadToken={reloadToken} goBack={() => setScreen("home")} />}
            {screen === "resumes" && <ResumesScreen onOpen={setEditTarget} reloadToken={reloadToken} goBack={() => setScreen("home")} />}
          </div>
        </div>
      )}

      {editorOpen && (
        <EditorView
          onClose={() => setEditorOpen(false)}
          onSaved={() => {
            flashSaved();
            bumpReload();
          }}
        />
      )}
      {editTarget && <EditWindow target={editTarget} onClose={() => setEditTarget(null)} onChanged={bumpReload} />}
      {settingsOpen && (
        <div className="editwin-scrim" onClick={() => setSettingsOpen(false)}>
          <div className="editwin" onClick={(e) => e.stopPropagation()}>
            <header className="editwin-head">
              <div className="editwin-title">Paramètres</div>
              <button className="iconbtn" onClick={() => setSettingsOpen(false)} aria-label="Fermer">
                <Icons.Close size={20} />
              </button>
            </header>
            <div className="editwin-body settings-win-body">
              <SettingsContent
                appearance={appearance}
                onLogout={() => void logout()}
                full={isDesktop}
                usage={usage}
                onChanged={bumpReload}
              />
            </div>
          </div>
        </div>
      )}
      {/* Rendu une seule fois ici : la génération peut être lancée depuis plusieurs écrans, et le
          bandeau de fin doit pouvoir ouvrir le résumé produit, ce que seul ce niveau sait faire. */}
      <SummaryToast
        onOpen={(summaryId, label) => {
          bumpReload();
          setEditTarget({ kind: "summary", id: summaryId, label });
        }}
      />
    </div>
  );
}
